// pages/resumes/ResumePolish.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiDownload, FiSave, FiLoader, FiBriefcase, FiBarChart2, FiTarget } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { resumeApi } from '../../services/api';
import { useMembership } from '../../contexts/MembershipContext';
import { useAuth } from '../../contexts/AuthContext';
import ProgressBar from '../../components/common/ProgressBar';
import { DisabledCreditButton } from '../../components/membership';
import SectionSelector from '../../components/resumes/SectionSelector';
import { useBalance } from '../../contexts/BalanceContext';
import { getResumeContentSafe } from '../../utils/resumeContentHelper';
import ResumeStyleModal from '../../components/resumes/ResumeStyleModal';
import PageHeader from '../../components/layout/PageHeader';
import { pageDescriptions } from '../../data/pageDescriptions';
import ScoreBar from '../../components/common/ScoreBar';
import ScoreRing from '../../components/common/ScoreRing';

const ResumePolish = () => {
  const { resumeId } = useParams();
  const navigate = useNavigate();
  const { refreshCreditBalance } = useMembership();
  const { currentUser } = useAuth();
  const { SERVICE_COSTS } = useBalance();
  
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polishing, setPolishing] = useState(false);
  const [jobDescription, setJobDescription] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [polishProgress, setPolishProgress] = useState(0);
  const [selectedSections, setSelectedSections] = useState([]);
  const [originalContent, setOriginalContent] = useState(null);
  const [polishedContent, setPolishedContent] = useState(null);
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [selectedExperienceIndexes, setSelectedExperienceIndexes] = useState([]);
  const [matchScore, setMatchScore] = useState(null);
  const [scoring, setScoring] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => { isMountedRef.current = false; };
  }, []);
  
  useEffect(() => {
    fetchResume();
  }, [resumeId]);
  
  useEffect(() => {
    if (currentUser?.default_polish_instructions && !additionalInfo) {
      setAdditionalInfo(currentUser.default_polish_instructions);
    }
  }, [currentUser, additionalInfo]);
  
  const fetchResume = async () => {
    try {
      setLoading(true);
      const response = await resumeApi.getResumeById(resumeId);
      setResume(response.data);
      
      // Get first version content
      if (response.data.versions && response.data.versions.length > 0) {
        const firstVersion = response.data.versions[0];
        setOriginalContent(firstVersion.content);
      }
    } catch (error) {
      console.error('Error fetching resume:', error);
      toast.error('Failed to load resume');
    } finally {
      setLoading(false);
    }
  };
  
  // Section selection is now handled by the SectionSelector component
  
// In the polishing function
const handlePolish = async () => {
  if (selectedSections.length === 0) {
    toast.warning('Please select at least one section to polish');
    return;
  }

  // Declare outside try so finally can always clear it
  let progressInterval = null;

  try {
    setPolishing(true);
    setPolishProgress(0);
    setMatchScore(null);

    // Simulate progress
    progressInterval = setInterval(() => {
      setPolishProgress(prev => {
        if (prev >= 90) return 90;
        return prev + Math.random() * 15;
      });
    }, 1000);

    // Get resume content using our helper function
    const currentContent = await getResumeContentSafe(resumeId, null, originalContent);

    const versionName = `AI Polished ${new Date().toLocaleDateString()}`;
    const experienceIndexes = selectedExperienceIndexes.length > 0 ? selectedExperienceIndexes : null;
    const response = await resumeApi.polishResume(
      resumeId,
      selectedSections,
      jobDescription || undefined,
      versionName,
      additionalInfo || null,
      currentContent,
      experienceIndexes
    );

    setPolishedContent(response.data.content);

    // Refresh credit balance after operation
    await refreshCreditBalance();

    if (jobDescription) {
      toast.success('Resume polished successfully! 🎉 Your resume has been tailored to the job description.');
    } else {
      toast.success('Resume polished successfully! 💡 Tip: Add a job description to get better results.');
    }
    setPolishProgress(100);

  } catch (error) {
    console.error('Error polishing resume:', error);
    if (error.response && error.response.status === 402) {
      toast.error('Not enough credits. Please upgrade your plan or purchase more credits.');
    } else {
      toast.error('Failed to polish resume');
    }
  } finally {
    clearInterval(progressInterval);
    setTimeout(() => {
      if (isMountedRef.current) {
        setPolishing(false);
        setPolishProgress(0);
      }
    }, 500);
  }
};
  
  const navigateToEditor = () => {
    navigate(`/resumes/${resumeId}`);
  };

  const contentToText = (content) => {
    if (!content) return '';
    if (typeof content === 'string') return content;
    let parts = [];
    if (content.header?.name) parts.push(content.header.name);
    if (content.summary) parts.push(content.summary);
    if (Array.isArray(content.experience)) {
      content.experience.forEach(exp => {
        if (exp.position) parts.push(exp.position);
        if (exp.company) parts.push(exp.company);
        if (Array.isArray(exp.details)) parts.push(...exp.details);
      });
    }
    if (content.skills) {
      if (Array.isArray(content.skills)) {
        content.skills.forEach(s => {
          if (typeof s === 'string') parts.push(s);
          else if (s?.details) parts.push(...s.details.filter(d => typeof d === 'string'));
        });
      } else {
        parts.push(String(content.skills));
      }
    }
    if (Array.isArray(content.education)) {
      content.education.forEach(edu => {
        if (edu.institution) parts.push(edu.institution);
        if (edu.degree) parts.push(edu.degree);
      });
    }
    return parts.join('\n');
  };

  const handleRecalculateMatchScore = async () => {
    if (!polishedContent || !jobDescription) return;
    try {
      setScoring(true);
      setMatchScore(null);
      const resumeText = contentToText(polishedContent);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/ats/score`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            resume_text: resumeText,
            job_description: jobDescription
          })
        }
      );
      if (!response.ok) {
        if (response.status === 402) {
          toast.error('Not enough credits. Please upgrade your plan or purchase more credits.');
          return;
        }
        throw new Error('Match score calculation failed');
      }
      const result = await response.json();
      setMatchScore(result);
      await refreshCreditBalance();
      toast.success('Match score calculated!');
    } catch (error) {
      console.error('Error calculating match score:', error);
      toast.error('Failed to calculate match score');
    } finally {
      setScoring(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }
  
  if (!resume) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Resume not found</h2>
        <p className="mt-2 text-gray-600">The resume you're looking for does not exist.</p>
        <div className="mt-6">
          <Link
            to="/resumes"
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <FiArrowLeft className="-ml-1 mr-2 h-5 w-5" />
            Back to Resumes
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div className="flex-1 min-w-0">
          <PageHeader
            title={`Polish Resume: ${resume.name}`}
            description={pageDescriptions.resumePolish.description}
            whatYouCanDo={pageDescriptions.resumePolish.whatYouCanDo}
            whatYouShouldDo={pageDescriptions.resumePolish.whatYouShouldDo}
            actions={
              <div className="flex items-center gap-2">
                <Link
                  to={`/resumes/${resumeId}`}
                  className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
                >
                  <FiArrowLeft className="h-5 w-5" />
                </Link>
                {polishedContent && (
                  <>
                    <button
                      type="button"
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      onClick={navigateToEditor}
                    >
                      <FiEdit className="-ml-1 mr-2 h-4 w-4" />
                      Go to Editor
                    </button>
                    <Link
                      to="/jobs"
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <FiBriefcase className="-ml-1 mr-2 h-4 w-4" />
                      Check Job Match
                    </Link>
                  </>
                )}
              </div>
            }
          />
        </div>
      </div>
      
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Selection and Polish */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-lg font-medium text-gray-900">
              Polish Your Resume
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Select the sections you want to polish and optionally provide a job description to tailor your resume
            </p>
            
            <div className="mt-6 space-y-6">
              <div>
                <label className="text-base font-medium text-gray-900">
                  Sections to Polish
                </label>
                <p className="text-sm text-gray-500">
                  Select which sections of your resume you want to enhance
                </p>
                
                <div className="mt-4">
                  <SectionSelector 
                    selectedSections={selectedSections} 
                    onChange={setSelectedSections} 
                  />
                </div>
              </div>

              <div>
                <label className="text-base font-medium text-gray-900">
                  Experience Items (Optional)
                </label>
                <p className="text-sm text-gray-500">
                  Choose specific experiences to polish. Leave empty to polish all experiences.
                </p>
                <div className="mt-3 space-y-2">
                  {(originalContent?.experience || []).length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No experience items found.</p>
                  ) : (
                    (originalContent?.experience || []).map((exp, index) => {
                      const label = [exp.position, exp.company].filter(Boolean).join(' at ') || `Experience ${index + 1}`;
                      const isChecked = selectedExperienceIndexes.includes(index);
                      return (
                        <label key={`exp-${index}`} className="flex items-center space-x-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                            checked={isChecked}
                            onChange={(e) => {
                              setSelectedExperienceIndexes(prev => {
                                if (e.target.checked) {
                                  return [...prev, index];
                                }
                                return prev.filter(i => i !== index);
                              });
                            }}
                          />
                          <span>{label}</span>
                        </label>
                      );
                    })
                  )}
                </div>
                {(originalContent?.experience || []).length > 0 && (
                  <div className="mt-3 flex space-x-2">
                    <button
                      type="button"
                      className="text-xs text-primary-600 hover:text-primary-700"
                      onClick={() => setSelectedExperienceIndexes((originalContent?.experience || []).map((_, i) => i))}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="text-xs text-gray-600 hover:text-gray-700"
                      onClick={() => setSelectedExperienceIndexes([])}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
              
              <div>
                <label htmlFor="job-description" className="block text-sm font-medium text-gray-700">
                  Job Description (Optional)
                </label>
                <div className="mt-1">
                  <textarea
                    id="job-description"
                    name="job-description"
                    rows={5}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Paste job description here to tailor your resume..."
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Adding a job description helps AI tailor your resume to specific requirements
                </p>
              </div>
              
              <div>
                <label htmlFor="additional-info" className="block text-sm font-medium text-gray-700">
                  Additional Instructions (Optional)
                </label>
                <div className="mt-1">
                  <textarea
                    id="additional-info"
                    name="additional-info"
                    rows={3}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Example: Keep tone concise, highlight leadership, avoid changing dates."
                    value={additionalInfo}
                    onChange={(e) => setAdditionalInfo(e.target.value)}
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Defaults to your saved polish instructions if set in Settings.
                </p>
              </div>
              
              <div>
                <DisabledCreditButton
                  serviceType="resume_polish"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  onClick={handlePolish}
                  disabled={polishing || selectedSections.length === 0}
                >
                  {polishing ? (
                    <>
                      <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      Polishing...
                    </>
                  ) : (
                    <>
                      <FiEdit className="-ml-1 mr-2 h-4 w-4" />
                      Polish Resume ({SERVICE_COSTS['resume_polish']} credits)
                    </>
                  )}
                </DisabledCreditButton>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right Column: Results */}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-lg font-medium text-gray-900">
              Polished Results
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {polishedContent 
                ? "Here's your polished resume content" 
                : "Your polished content will appear here after processing"}
            </p>
            
            {!polishedContent && !polishing && (
              <div className="mt-6 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center bg-gradient-to-br from-gray-50 to-white">
                <FiEdit className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No polished content yet</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Select sections to polish and click the "Polish Resume" button
                </p>
              </div>
            )}
            
            {polishing && (
              <div className="mt-6 border-2 border-gray-300 rounded-lg p-12 text-center">
                <FiLoader className="mx-auto h-12 w-12 text-primary-500 animate-spin" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Polishing your resume...</h3>
                <p className="mt-1 text-sm text-gray-500">
                  This may take a moment as our AI enhances your content
                </p>
                <div className="mt-4 relative" style={{ minHeight: '100px' }}>
                  <ProgressBar progress={polishProgress} showPercentage={false} />
                  <p className="text-sm text-gray-500 mt-16 text-center">
                    {Math.round(polishProgress)}% complete
                  </p>
                </div>
              </div>
            )}
            
            {polishedContent && !polishing && (
              <div className="mt-6 space-y-6">
                {selectedSections.includes('summary') && (
                  <div className="rounded-md bg-green-50 p-4">
                    <div className="flex">
                      <div>
                        <h3 className="text-sm font-medium text-green-800">Professional Summary</h3>
                        <div className="mt-2 text-sm text-green-700">
                          <p>{polishedContent.summary}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {selectedSections.includes('experience') && polishedContent.experience && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Work Experience</h3>
                    <div className="mt-2 space-y-4">
                      {polishedContent.experience.map((exp, index) => (
                        <div key={index} className="rounded-md bg-green-50 p-4">
                          <div className="flex justify-between">
                            <div>
                              <h4 className="text-sm font-medium text-green-800">{exp.position} at {exp.company}</h4>
                              <p className="text-xs text-green-700">{exp.start} - {exp.end || 'Present'}</p>
                            </div>
                          </div>
                          <ul className="mt-2 list-disc pl-5 text-sm text-green-700 space-y-1">
                            {(exp.details || []).map((detail, detailIndex) => (
                              <li key={detailIndex}>{detail}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedSections.includes('skills') && polishedContent.skills && (
                  <div className="rounded-md bg-green-50 p-4">
                    <div className="flex">
                      <div className="w-full">
                        <h3 className="text-sm font-medium text-green-800">Skills</h3>
                        <div className="mt-2 text-sm text-green-700">
                          {(() => {
                            const skills = polishedContent.skills;
                            const isGrouped = Array.isArray(skills) && skills.length > 0 && skills.every((s) => s && typeof s === 'object' && Array.isArray(s.details));
                            if (isGrouped) {
                              return (
                                <div className="space-y-2">
                                  {skills.map((group, gi) => (
                                    <div key={gi}>
                                      {group.type && <span className="font-medium text-green-800 mr-2">{group.type}</span>}
                                      <span className="flex flex-wrap gap-1.5 mt-1">
                                        {(group.details || []).filter((d) => typeof d === 'string').map((skill, si) => (
                                          <span key={si} className="inline-flex px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs">{skill}</span>
                                        ))}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                            return <p>{Array.isArray(skills) ? skills.join(', ') : String(skills)}</p>;
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {jobDescription && (
                  <div className="relative rounded-2xl overflow-hidden">
                    <div
                      className="absolute -inset-px rounded-2xl pointer-events-none"
                      style={{
                        background: matchScore
                          ? 'linear-gradient(135deg, rgba(90,175,167,0.25), rgba(6,182,212,0.15), rgba(90,175,167,0.1))'
                          : 'linear-gradient(135deg, rgba(209,213,219,0.5), rgba(229,231,235,0.3))',
                        transition: 'background 0.6s ease',
                      }}
                    />
                    <div className="relative rounded-2xl bg-gradient-to-br from-white via-white to-gray-50/80 p-5">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2.5">
                          <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-primary-50 to-cyan-50 ring-1 ring-primary-200/30">
                            <FiTarget className="h-3.5 w-3.5 text-primary-600" />
                          </div>
                          <span className="text-sm font-semibold text-gray-700">
                            {matchScore ? 'Job Match Score' : 'Check Job Match'}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="inline-flex items-center px-3.5 py-1.5 text-xs font-medium rounded-lg text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-400 disabled:opacity-50 shadow-sm shadow-primary-500/20 transition-all duration-200"
                          onClick={handleRecalculateMatchScore}
                          disabled={scoring}
                        >
                          {scoring ? (
                            <>
                              <FiLoader className="animate-spin -ml-0.5 mr-1.5 h-3.5 w-3.5" />
                              Calculating...
                            </>
                          ) : (
                            matchScore ? 'Recalculate' : 'Calculate Match Score'
                          )}
                        </button>
                      </div>

                      {matchScore && (
                        <>
                          <div className="h-px bg-gradient-to-r from-transparent via-primary-200/40 to-transparent mb-5" />
                          <div className="flex flex-col items-center gap-5">
                            <ScoreRing
                              score={matchScore.overall_score}
                              size={136}
                              strokeWidth={10}
                              label="/ 100"
                              showBadge
                            />
                            <div className="w-full space-y-3 rounded-xl bg-gray-50/60 p-4 ring-1 ring-gray-200/40">
                              {matchScore.keyword_analysis && (() => {
                                const matched = matchScore.keyword_analysis.matched?.length || 0;
                                const missing = matchScore.keyword_analysis.missing?.length || 0;
                                const total = matched + missing;
                                const pct = total > 0 ? Math.round((matched / total) * 100) : 0;
                                return (
                                  <div className="space-y-1.5">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs font-medium text-gray-500">Keywords</span>
                                      <span className="text-xs tabular-nums text-gray-400">{matched}/{total}</span>
                                    </div>
                                    <ScoreBar score={pct} label="" showValue size="md" />
                                  </div>
                                );
                              })()}
                              {matchScore.formatting_score !== undefined && (
                                <div className="space-y-1.5">
                                  <span className="text-xs font-medium text-gray-500">Formatting</span>
                                  <ScoreBar score={matchScore.formatting_score} label="" showValue size="md" />
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 text-center leading-relaxed max-w-[280px] italic">
                              {matchScore.overall_score >= 80 ? 'Great match! Your resume is well-optimized for this position.' :
                               matchScore.overall_score >= 60 ? 'Good match, but there\'s room for improvement.' :
                               'Needs improvement. Consider tailoring your resume more closely to this job.'}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    type="button"
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    onClick={navigateToEditor}
                  >
                    <FiEdit className="-ml-1 mr-2 h-4 w-4" />
                    Edit Resume
                  </button>
                  
                  <button
                    type="button"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    onClick={() => setShowStyleModal(true)}
                  >
                    <FiDownload className="-ml-1 mr-2 h-4 w-4" />
                    Download PDF
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Resume Style Modal */}
      <ResumeStyleModal
        show={showStyleModal}
        onClose={() => setShowStyleModal(false)}
        resumeId={resumeId}
        versionId={null}
      />
    </div>
  );
};

export default ResumePolish;